import { useState, useEffect, useCallback, useRef } from "react";
import { debounce } from "lodash";
import { useProject, useNotification, useApiResponseParser } from "../../../../hooks";
import { useInspectionAttributes } from "../../../../hooks/query";

// ─── Types ───────────────────────────────────────────────────────────────────

export interface GutterScopeState {
    removeExistingGutters: boolean;
    installNewGutters: boolean;
    gutterSize: string;
    gutterColor: string;
    removeExistingDownspouts: boolean;
    installNewDownspouts: boolean;
    downspoutSize: string;
    downspoutColor: string;
    gutterLength: string;
    downspoutLength: string;
}

// ─── Constants ───────────────────────────────────────────────────────────────

// Maps each local state key to its backend AttributeName
export const ATTRIBUTE_NAMES: Record<keyof GutterScopeState, string> = {
    removeExistingGutters: "Remove Existing Gutters",
    installNewGutters: "Install New Gutters",
    gutterColor: "Gutter Color",
    removeExistingDownspouts: "Remove Existing Downspouts",
    installNewDownspouts: "Install New Downspouts",
    downspoutColor: "Downspout Color",
    gutterSize: "Gutter Size",
    downspoutSize: "Downspout Size",
    gutterLength: "Gutter Length",
    downspoutLength: "Downspout Length",
};

export const INITIAL_STATE: GutterScopeState = {
    removeExistingGutters: false,
    installNewGutters: false,
    gutterSize: "",
    gutterColor: "",
    removeExistingDownspouts: false,
    installNewDownspouts: false,
    downspoutSize: "",
    downspoutColor: "",
    gutterLength: "",
    downspoutLength: "",
};

// ─── Measurement Keys ────────────────────────────────────────────────────────
// When gutter measurements are added to the DB, they will use these keys.
// These will be used as fallback defaults if no user-edited value exists.
const MEASUREMENT_FALLBACK_KEYS: Partial<Record<keyof GutterScopeState, string>> = {
    gutterLength: "GutterLength",
    downspoutLength: "DownspoutLength",
};

// ─── Pure Functions ──────────────────────────────────────────────────────────

/** Extracts a measurement value from the measurements array by its MeasurementType key */
function getMeasurementValue(measurements: any[], measurementType: string): string {
    const match = measurements?.find((m: any) => m.MeasurementType === measurementType);
    const value = match?.ActualValue ?? match?.CalculatedValue;
    return value != null ? String(value) : "";
}

/** Parses a flat list of API attributes into the GutterScopeState shape.
 *  Falls back to the measurement value if no user-edited attribute has been saved yet. */
function parseAttributesToState(
    attrs: any[],
    measurements: any[],
    attributeIds: Record<string, string>,
): Partial<GutterScopeState> {
    const loaded: Partial<GutterScopeState> = {};

    attrs.forEach((attr: any) => {
        const stateKey = Object.keys(ATTRIBUTE_NAMES).find(
            (key) => ATTRIBUTE_NAMES[key as keyof GutterScopeState] === attr.AttributeName,
        ) as keyof GutterScopeState | undefined;

        if (!stateKey) return;

        // Track the DB ID so we can update rather than create later
        attributeIds[attr.AttributeName] = attr.Id;

        const isBooleanField = typeof INITIAL_STATE[stateKey] === "boolean";
        (loaded as any)[stateKey] = isBooleanField
            ? attr.AttributeValue === "true" || attr.AttributeValue === true
            : attr.AttributeValue || "";
    });

    // ─── Measurement Fallbacks ───────────────────────────────────────────────
    // For length fields: if the user hasn't saved a value yet, pre-fill from
    // the measurements array. When gutter measurements are added to the DB
    // as a MeasurementType, they will automatically appear here as defaults.
    Object.entries(MEASUREMENT_FALLBACK_KEYS).forEach(([stateKey, measurementType]) => {
        const key = stateKey as keyof GutterScopeState;
        const alreadyHasValue = loaded[key] && String(loaded[key]).trim() !== "";
        if (!alreadyHasValue) {
            const fallback = getMeasurementValue(measurements, measurementType);
            if (fallback) (loaded as any)[key] = fallback;
        }
    });

    return loaded;
}

/** Resolves the correct persistence strategy: update if ID known, else refresh and update, else create. */
async function persistAttribute(
    attributeName: string,
    value: string | boolean,
    projectId: string,
    attributeIds: Record<string, string>,
    services: { listSvc: any; updateSvc: any; createSvc: any },
) {
    const stringValue = String(value);
    const existingId = attributeIds[attributeName];
    const { listSvc, updateSvc, createSvc } = services;

    if (existingId) {
        await updateSvc.mutateAsync({ Id: existingId, Value: stringValue });
        return;
    }

    // ID not cached — fetch fresh list and try to find it
    const attrs = await listSvc.mutateAsync({ ProjectId: projectId });
    const match = attrs.find((a: any) => a.AttributeName === attributeName);

    if (match) {
        await updateSvc.mutateAsync({ Id: match.Id, Value: stringValue });
        attributeIds[attributeName] = match.Id;
    } else {
        // Attribute was never created — create it as a fallback
        await createSvc.mutateAsync({ ProjectId: projectId, [attributeName]: stringValue });
    }
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useGutter(measurements: any[] = []) {
    const { project } = useProject();
    const { showError } = useNotification();
    const { parseErrorResponseData } = useApiResponseParser();
    const { useCreate, useListByProjectId, useUpdateSingleAttribute } = useInspectionAttributes();

    const createSvc = useCreate();
    const listSvc = useListByProjectId();
    const updateSvc = useUpdateSingleAttribute();

    const [state, setState] = useState<GutterScopeState>(INITIAL_STATE);
    const [isLoading, setIsLoading] = useState(true);

    const attributeIds = useRef<Record<string, string>>({});
    const isInitialLoad = useRef(true);

    // ─── Effect: Load on project change ──────────────────────────────────────

    useEffect(() => {
        const loadGutterData = async () => {
            if (!project?.id) {
                setIsLoading(false);
                return;
            }
            isInitialLoad.current = true;
            try {
                const attrs = await listSvc.mutateAsync({ ProjectId: project.id });
                const loaded = parseAttributesToState(attrs, measurements, attributeIds.current);
                setState((prev) => ({ ...prev, ...loaded }));
            } catch (error: any) {
                showError(parseErrorResponseData(error) || "Failed to load gutter details.");
            } finally {
                setIsLoading(false);
                setTimeout(() => { isInitialLoad.current = false; }, 800);
            }
        };

        loadGutterData();
    }, [project?.id]);

    // ─── Callback: Save one attribute to the backend ─────────────────────────

    const saveAttribute = useCallback(
        async (attributeName: string, value: string | boolean) => {
            if (!project?.id) return;
            try {
                await persistAttribute(attributeName, value, project.id, attributeIds.current, {
                    listSvc,
                    updateSvc,
                    createSvc,
                });
            } catch (error: any) {
                showError(parseErrorResponseData(error) || `Failed to save ${attributeName}.`);
            }
        },
        [project?.id, createSvc, updateSvc, listSvc, parseErrorResponseData, showError],
    );

    const debouncedSave = useRef(debounce(saveAttribute, 500)).current;

    // ─── Public: Called by GutterScope on every UI change ────────────────────

    const updateGutterState = useCallback(
        <K extends keyof GutterScopeState>(key: K, value: GutterScopeState[K]) => {
            setState((prev) => ({ ...prev, [key]: value }));

            if (isInitialLoad.current) return;

            const attributeName = ATTRIBUTE_NAMES[key];
            typeof value === "boolean"
                ? saveAttribute(attributeName, value)
                : debouncedSave(attributeName, value);
        },
        [saveAttribute, debouncedSave],
    );

    return { state, updateGutterState, isLoading };
}
